<template>
  <span>
    <!-- Header item (clickable for collapse/expand) — no animation; the
         transition belongs to the leaves that toggle, not to the head
         that controls the toggle. -->
    <v-list-item
      v-if="item.isHeader && isHeaderVisible"
      :class="[
        'menu-header tw-cursor-pointer tw-whitespace-nowrap',
        item.parentGroupId ? 'sub-menu-header' : ''
      ]"
      @click="toggleGroup"
    >
      <v-list-item-icon>
        <img v-if="item.icon" :src="'/images/icons/' + item.icon" class="menu-icon" />
      </v-list-item-icon>
      <div class="container-list tw-flex tw-items-center tw-justify-between">
        <v-list-item-title class="menu-header__title tw-font-normal">{{ item.text }}</v-list-item-title>
        <v-icon
          size="20"
          class="menu-header__chevron"
          :class="{ 'menu-header__chevron--expanded': isExpanded }"
        >mdi-chevron-down</v-icon>
      </div>
    </v-list-item>

    <!-- Disabled menu item (non-clickable) — leaves toggle visibility when
         their group collapses; the .menu-leaf transition animates opacity
         + max-height in sync across all leaves of the same group. -->
    <transition name="menu-leaf">
      <v-list-item
        v-if="!item.isHeader && item.disabled && isVisible"
        :class="[
          'disabled-menu-item',
          item.groupId ? 'sub-menu-item' : ''
        ]"
        aria-disabled="true"
        tabindex="-1"
      >
        <v-list-item-icon>
          <img
            v-if="item.icon"
            :src="'/images/icons/' + item.icon"
            class="menu-icon"
          />
        </v-list-item-icon>
        <div class="container-list">
          <v-list-item-title v-if="!item.icon" class="tw-ml-1 tw-flex tw-gap-2 tw-items-center tw-font-normal">
            {{ item.text }}
          </v-list-item-title>
          <v-list-item-title v-else class="tw-font-normal">
            {{ item.text }}
          </v-list-item-title>
        </div>
      </v-list-item>
    </transition>

    <!-- Regular clickable menu item -->
    <transition name="menu-leaf">
      <v-list-item
        v-if="!item.isHeader && !item.disabled && isVisible"
        link
        :to="{ name: item.link, params: item.vendorCode ? {
          vendorId: item.vendorCode
        } : {
          endsWith: item.endsWith
        }}"
        :class="[
          ((item.link == 'Company.DocumentPOAI' || item.link == 'Company.DocumentSOAI')  && !isPdfDoc) || item.link === 'Company.DocumentsPurchase' && companyID === 'pvfyTLwab5Dsef5fKEnV'
            ? 'hide'
            : '',
          item.groupId ? 'sub-menu-item' : '',
          item.icon && !item.groupId ? 'top-level-head' : ''
        ]"
        :aria-current="isActive ? 'page' : null"
      >
        <v-list-item-icon>
          <img
            v-if="item.icon"
            :src="'/images/icons/' + item.icon"
            class="menu-icon"
          />
          <img
            v-if="item.icon_active"
            :src="'/images/icons/' + item.icon_active"
            class="menu-icon"
          />
        </v-list-item-icon>
        <div class="container-list">
          <v-list-item-title  v-if="!item.icon" class="tw-ml-1 tw-flex tw-gap-2 tw-items-center tw-font-normal">
            <img
            v-if="item.subIcon"
            :src="item.subIcon"
            class="menu-icon menu-icon--inline"
          />
            {{ item.text }}
          </v-list-item-title>
          <v-list-item-title v-else class="tw-font-normal">
            {{ item.text }}
          </v-list-item-title>
        </div>
      </v-list-item>
    </transition>

    <!--  <v-list-item v-for="submenu in item.subLinks" :key="submenu.text" link :to="{name: submenu.link}">-->
    <!--    <v-list-item-icon></v-list-item-icon>-->
    <!--    <v-list-item-content>-->
    <!--      <v-list-item-title>-->
    <!--        {{ submenu.text }}-->
    <!--      </v-list-item-title>-->
    <!--    </v-list-item-content>-->
    <!--  </v-list-item>-->
  </span>
</template>

<script>
export default {
  name: "list",
  props: ["item", "companyID","isPdfDoc", "groupState"],
  computed: {
    isHeaderVisible() {
      // If header has parentGroupId, check if parent group is expanded
      if (this.item.isHeader && this.item.parentGroupId) {
        return this.groupState && this.groupState[this.item.parentGroupId];
      }
      // Regular header without parent, always show
      return true;
    },
    isVisible() {
      // If this item has no groupId (not part of collapsible group), always show
      if (!this.item.groupId) return true;

      // Check if parent group is expanded (for nested groups)
      const parentExpanded = !this.item.parentGroupId || (this.groupState && this.groupState[this.item.parentGroupId]);

      // Check if own group is expanded
      const ownGroupExpanded = this.groupState && this.groupState[this.item.groupId];

      return parentExpanded && ownGroupExpanded;
    },
    isActive() {
      return this.item.link && this.$route && this.$route.name === this.item.link;
    },
    // Derived from the parent's groupState, NOT local data. The old
    // version held `isExpanded` as a local data field defaulted to true
    // and re-emitted it from `mounted()` to initialize the parent —
    // which meant any re-render that destroyed and re-created this
    // component instance (e.g. selectedCompanyProductList reactivity
    // tick after a route change) reset isExpanded to true and snapped
    // the user's collapsed group back open. Reading from groupState
    // makes the parent the single source of truth; the child can't
    // desync.
    isExpanded() {
      if (!this.item.groupId || !this.groupState) return true;
      // undefined → expanded (parent doesn't yet know this group)
      return this.groupState[this.item.groupId] !== false;
    }
  },
  methods: {
    toggleGroup() {
      if (this.item.isHeader && this.item.groupId) {
        this.$emit('toggle-group', this.item.groupId, !this.isExpanded);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.container-list {
  // border: 2px solid red ;
  width: 100%;
  transform: translate(-20px);
}

.hide {
  display: none;
}

// ─────────────────────────────────────────────────────────────────────
// Menu design tokens — single source of truth for icon / font sizing
// so headers, leaves, and inline logos never drift again.
//
//   Icon (square 20)  Chevron 20  Header 17  Leaf 15
//
// Previous inconsistencies fixed:
//   * `.menu-icon` was 22×20 (non-square) with `margin-left: 20%`
//     (percentage — moved with parent width). Now 20×20 with fixed
//     centering via flex on the v-list-item icon column.
//   * Inline vendor logos used `tw-w-6 tw-h-6` (24×24) — different
//     from the main icon. Now they use `.menu-icon.menu-icon--inline`
//     so the whole app pulls from one class.
// ─────────────────────────────────────────────────────────────────────
.menu-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

// Inline variant (rendered next to the title text, not in the icon
// column). Overrides the auto-centering margin because the flex row
// around it handles gap explicitly.
.menu-icon--inline {
  margin: 0;
  flex: 0 0 auto;
}

// Force the v-list-item icon column to a fixed square footprint so
// icons sit at the same X across every row regardless of the row's
// content. Vuetify's default min-width is 24 px, which paired with
// the old 20% margin-left produced variable horizontal alignment.
.v-list-item__icon {
  min-width: 40px !important;
  width: 40px !important;
  justify-content: center;
  align-items: center;
  // Pull the label closer to the icon. Vuetify's default is a 32px gap
  // (.v-list-item__icon:first-child { margin-right: 32px }); 20px tightens the
  // icon+label pairing while keeping some breathing room.
  margin-right: 20px !important;
}

.v-list-item--link:before {
  background-color: #ffffff;
}

.v-list-item__icon {
  img:nth-child(2) {
    display: none;
  }
}

.v-list-item {
  margin-left: 5px;
  min-height: 40px;
}

// Bump leaf text size — Vuetify default v-list-item-title is ~14px which
// reads small for the nav at desktop width. 15px keeps hierarchy with the
// 16px head while staying compact.
.v-list-item__title {
  font-size: 15px !important;
}

// Custom CSS expand/collapse for sub-menu leaves. Avoids Vuetify's
// v-expand-transition because its JS-driven height calc was jittering
// when multiple leaves animated at once. Pure CSS = predictable timing
// across every leaf in a group (Vue triggers all transitions in the same
// tick, and same-curve same-duration means visually one motion).
//
// max-height instead of height: lets natural content height vary without
// JS measurement. 64px ceiling fits the 40px item + room for padding.
.menu-leaf-enter-active,
.menu-leaf-leave-active {
  transition:
    opacity 160ms ease-out,
    max-height 200ms cubic-bezier(0.16, 1, 0.3, 1),
    margin-top 200ms cubic-bezier(0.16, 1, 0.3, 1),
    margin-bottom 200ms cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
}
.menu-leaf-enter,
.menu-leaf-leave-to {
  opacity: 0;
  max-height: 0 !important;
  min-height: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
.menu-leaf-enter-to,
.menu-leaf-leave {
  opacity: 1;
  max-height: 64px;
}

// Sub-menu items — clean minimalist (no tree rail, no bullets).
// Hierarchy comes from indent + hover + the active pill alone.
// Indent trimmed 40→28 px so leaves sit closer to the header text
// column and the drawer reads as a tighter, less-nested list.
.sub-menu-item {
  padding-left: 28px !important;
  transition: background-color 120ms ease-out;

  &:hover:not(.v-list-item--active) {
    background-color: rgba(0, 0, 0, 0.03);
  }

  &.v-list-item--active {
    margin-left: 5px !important;
    padding-left: 28px !important;
  }
}

// Top-level "head" items — flat menu entries that visually act like
// section headers (they carry an icon and sit at the outermost tier
// but the data model didn't mark them as `isHeader`, so the .menu-header
// 17 px rule never reached them). etax's หน้าสรุป / เอกสารการขาย /
// เอกสารการซื้อ / รายงาน + Fixed Asset's top row live here — the class
// is added in the template when `item.icon && !item.groupId`, so any
// leaf that IS grouped (AP Automate leaves have both icon + groupId)
// keeps the leaf font size via .sub-menu-item.
.top-level-head .v-list-item__title {
  font-size: 17px !important;
  letter-spacing: 0.3px;
  color: #1a1a1a;
}

// When a top-level-head row is the active route, the red active pill needs
// white text. The rule above sets a dark title with !important, so the
// active override has to beat it on both specificity and !important (the
// icon already swaps to its white _active variant separately).
.v-list-item--active.top-level-head .v-list-item__title {
  color: #ffffff !important;
}

// Header styling — hierarchy comes from size + color + spacing
// (NOT weight), since bold was rejected by the design owner. Section
// rhythm is created by margin-top + a subtle inset divider above each
// top-level header.
.menu-header {
  user-select: none;
  transition: background-color 150ms ease-out;

  // Top-level heads only — sub-menu-header (nested) overrides below.
  //
  // Hierarchy pass:
  //   - Header sits at the same vertical rhythm as leaves (no extra
  //     margin-top) so the drawer reads as one continuous list; the
  //     17 px title + tracking is the only differentiator now.
  //   - No section divider — the whitespace-free layout meant the
  //     ::before line either overlapped the previous row or read as a
  //     stray rule inside it, so it's dropped.
  &:not(.sub-menu-header) {
    .menu-header__title {
      font-size: 17px !important;
      letter-spacing: 0.3px;
      color: #1a1a1a;
    }
  }

  &:hover {
    background-color: rgba(201, 36, 43, 0.04);
  }
}

// Single chevron rotated for collapse/expand — smoother than swapping
// two MDI icons. Reads as a single intentional motion.
.menu-header__chevron {
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
  color: #999 !important;
}
.menu-header__chevron--expanded {
  transform: rotate(180deg);
}


// Sub-menu header styling (for headers that are subtabs)
.sub-menu-header {
  padding-left: 40px !important;
  font-weight: 400 !important;
  color: #d9101a !important;
}

@media (prefers-reduced-motion: reduce) {
  .menu-header,
  .menu-header__chevron {
    transition: none;
  }
}

.disabled-menu-item {
  opacity: 1;
  pointer-events: none;
  cursor: default;
}

// NS TST default block uses `disabled: true` items as "visual headers"
// (line 62-135 of drawer.js), not the `isHeader` collapsible pattern.
// Those items don't get the `.menu-header` class so the Phase-1 font
// boost and section divider don't reach them — the header line reads
// at 15 px just like the leaves below it and the hierarchy collapses.
// Selector excludes `.sub-menu-item` because disabled items that ARE
// part of a group (rare, but possible) already belong to a leaf row
// and should keep the leaf typography.
.disabled-menu-item:not(.sub-menu-item) {
  .v-list-item__title {
    font-size: 17px !important;
    letter-spacing: 0.3px;
    color: #1a1a1a;
  }
}

.v-list-item--active:first-child {
  background-color: #c9242b;
  color: #ffffff;
  position: relative;
  border-top-left-radius: 30px;
  border-bottom-left-radius: 30px;
  margin-left: 5px;

  //&:before {
  //  opacity: 1;
  //  content: "";
  //  width: 4px;
  //  position: absolute;
  //  left: 0;
  //  top: 0;
  //  bottom: 0;
  //  background-color: #c9242b;
  //  border-top-right-radius: 5px;
  //  border-bottom-right-radius: 5px;
  //}

  // Keep the SAME icon glyph on select and only recolor it white, instead of
  // swapping to the icon_active file. Some items point icon_active at an
  // unrelated glyph (e.g. ใบสั่งขายออนไลน์ / เอกสารการซื้อ →
  // icon_menu_tax_active_white.svg), so the swap changed the icon's shape on
  // select, not just its colour. brightness(0) invert(1) turns the monochrome
  // line icon pure white; the icon_active <img> is hidden so it can't diverge.
  .v-list-item__icon {
    img:nth-child(1) {
      display: block;
      filter: brightness(0) invert(1);
    }

    img:nth-child(2) {
      display: none;
    }
  }
}
</style>
